/* ==========================================================================
   Lowden Guitars — Sitewide JavaScript
   ========================================================================== 
   
   A unified JS file handling all sitewide interactions for the Lowden Guitars
   Shopify theme. Consolidates inline scripts from all HTML templates into a
   single, clean, well-organized file.
   
   Dependencies: Swiper.js (loaded separately via CDN)
   
   Table of Contents:
   1.  Mobile Menu Toggle
   2.  Submenu Toggle
   3.  Sticky Header
   4.  Accordion Toggle
   5.  Fade-in on Scroll
   6.  Parallax Effect
   7.  Gallery Expand
   8.  Swiper Carousels
   9.  Location Selector (Cookie-based)
   10. Product Page Rotate Element
   11. Dock Model Specs
   ========================================================================== */

document.addEventListener('DOMContentLoaded', function () {

	/* ==========================================================================
	   1. Mobile Menu Toggle
	   --------------------------------------------------------------------------
	   Listens for clicks on [data-action="toggle_menu"] buttons and toggles the
	   'show' class on .mobile_menu to open/close the mobile navigation.
	   ========================================================================== */

	const mobileMenu = document.querySelector('.mobile_menu');
	const menuButtons = document.querySelectorAll('[data-action="toggle_menu"]');

	menuButtons.forEach(function (button) {
		button.addEventListener('click', function () {
			if (mobileMenu) {
				mobileMenu.classList.toggle('show');
			}
		});
	});


	/* ==========================================================================
	   2. Submenu Toggle
	   --------------------------------------------------------------------------
	   Listens for clicks on [data-action="toggle_submenu"] links within the
	   mobile menu to expand/collapse nested sub-menus by toggling 'open' class.
	   ========================================================================== */

	const submenuToggles = document.querySelectorAll('[data-action="toggle_submenu"]');

	submenuToggles.forEach(function (toggle) {
		toggle.addEventListener('click', function (event) {
			event.target.classList.toggle('open');
		});
	});


	/* ==========================================================================
	   3. Sticky Header
	   --------------------------------------------------------------------------
	   Handles two header modes based on body class:
	   
	   - standard_header pages: adds 'show_header' to body when scrollY > 150
	     (used on pages with a fixed_header that appears on scroll)
	   
	   - docked_header / all other pages: adds 'docked' to body when scrollY > 25
	     (used on pages where the header docks to top on scroll)
	   
	   Some pages (e.g. accessories) use a lower threshold of 25px, while most
	   use 150px. The body class determines which behavior applies.
	   ========================================================================== */

	const isStandardHeader = document.body.classList.contains('standard_header');
	const isDocked = document.body.classList.contains('docked_header');

	window.addEventListener('scroll', function () {
		const pageY = window.scrollY;

		if (isStandardHeader) {
			// Standard header pages: show fixed header after scrolling 150px
			if (pageY > 150) {
				document.body.classList.add('show_header');
			} else {
				document.body.classList.remove('show_header');
			}
		}

		if (isDocked) {
			// Docked header pages: dock the header after scrolling 25px
			if (pageY > 25) {
				document.body.classList.add('docked');
			} else {
				document.body.classList.remove('docked');
			}
		}

		// Pages without standard_header or docked_header class also use 'docked'
		// at 150px threshold (e.g. about, series, design pages)
		if (!isStandardHeader && !isDocked) {
			if (pageY > 150) {
				document.body.classList.add('docked');
			} else {
				document.body.classList.remove('docked');
			}
		}
	});


	/* ==========================================================================
	   4. Accordion Toggle
	   --------------------------------------------------------------------------
	   Listens for clicks on [data-action="toggle"] buttons (used on product
	   pages for specs/details accordions) and toggles the 'show' class on the
	   clicked element.
	   ========================================================================== */

	const accordionToggles = document.querySelectorAll('[data-action="toggle"]');

	accordionToggles.forEach(function (toggle) {
		toggle.addEventListener('click', function (event) {
			event.target.classList.toggle('show');
		});
	});


	/* ==========================================================================
	   5. Fade-in on Scroll
	   --------------------------------------------------------------------------
	   Elements with [data-action="fadein"] animate into view as they enter the
	   viewport. They start at opacity: 0 and translateY(50px), transitioning to
	   opacity: 1 and translateY(0) over a 250px scroll distance.
	   
	   Uses translate3d for GPU-accelerated compositing.
	   ========================================================================== */

	const FADE_DISTANCE = 250;

	function initFadeInElements() {
		const fadeElements = document.querySelectorAll('[data-action="fadein"]');

		if (fadeElements.length === 0) return;

		function adjustOpacityBasedOnScroll() {
			const windowHeight = window.innerHeight || document.documentElement.clientHeight;

			fadeElements.forEach(function (el) {
				const rect = el.getBoundingClientRect();
				const elementTop = rect.top;

				if (elementTop <= (windowHeight - 50)) {
					// Element is entering the viewport
					const scrolledRatio = ((windowHeight - 50) - elementTop) / FADE_DISTANCE;
					let transformPos = 50 - (50 * scrolledRatio);
					const opacity = Math.min(1, Math.max(0, scrolledRatio));

					if (opacity >= 1 || transformPos < 0) {
						// Element is fully visible — lock in final state
						el.style.transform = 'translate3d(0px, 0px, 0px)';
						el.style.opacity = 1;
					} else {
						el.style.transform = 'translate3d(0, ' + transformPos + 'px, 0)';
						el.style.opacity = opacity;
					}
				} else {
					// Element is below the viewport — keep hidden
					el.style.transform = 'translate3d(0px, 50px, 0px)';
					el.style.opacity = 0;
				}
			});
		}

		window.addEventListener('scroll', adjustOpacityBasedOnScroll);

		// Run once on init to handle elements already in view
		adjustOpacityBasedOnScroll();
	}


	/* ==========================================================================
	   6. Parallax Effect
	   --------------------------------------------------------------------------
	   Elements with [data-position="parallax"] (e.g. hero headings on the about
	   page) translate upward based on scroll position, creating a parallax depth
	   effect. Translation is scrollY / 8, rounded to nearest pixel.
	   ========================================================================== */

	function initParallaxElements() {
		const parallaxElements = document.querySelectorAll('[data-position="parallax"]');

		if (parallaxElements.length === 0) return;

		function updateParallax() {
			let currentScrollPos = Math.round(window.scrollY / 8);

			parallaxElements.forEach(function (el) {
				el.style.transform = 'translate3d(0, -' + currentScrollPos + 'px, 0)';
			});
		}

		window.addEventListener('scroll', updateParallax);

		// Run once on init
		updateParallax();
	}


	/* ==========================================================================
	   7. Gallery Expand
	   --------------------------------------------------------------------------
	   For the #target gallery element (homepage), expands its width from its
	   initial constrained width to full window width as the user scrolls past it.
	   
	   Once the element enters the viewport (250px from the bottom), the expansion
	   begins and completes over a 200px scroll distance. If the element is
	   already well into the viewport on load, it snaps to full width immediately.
	   ========================================================================== */

	function initGalleryExpand() {
		const target = document.getElementById('target');

		if (!target) return;

		const fullWidth = window.innerWidth;
		const initialWidth = target.offsetWidth;

		// Set explicit width and add the full_width class for CSS transition prep
		target.style.width = initialWidth + 'px';
		target.classList.add('full_width');

		let startScroll = 0;
		let isInView = false;

		function isElementInViewport(el) {
			const rect = el.getBoundingClientRect();
			return (rect.top <= window.innerHeight - 250);
		}

		function checkElementInView() {
			if (isElementInViewport(target)) {
				if (!isInView) {
					isInView = true;

					const rect = target.getBoundingClientRect();

					if (rect.top <= window.innerHeight - 500) {
						// Already well past the trigger — snap to full width
						target.style.width = fullWidth + 'px';
					} else {
						// Just entering — begin gradual expansion
						startScroll = window.scrollY;
					}
				}
			} else {
				isInView = false;
			}
		}

		function expandOnScroll() {
			if (isInView) {
				const scrolledDistance = window.scrollY - startScroll;
				const maxDistance = 200;

				if (scrolledDistance >= 0 && scrolledDistance <= maxDistance) {
					const newWidth = initialWidth + (fullWidth - initialWidth) * (scrolledDistance / maxDistance);
					target.style.width = newWidth + 'px';
				} else if (scrolledDistance > maxDistance) {
					target.style.width = fullWidth + 'px';
				}
			}
		}

		window.addEventListener('scroll', checkElementInView);
		window.addEventListener('scroll', expandOnScroll);

		// Check on init in case already in view
		checkElementInView();
	}


	/* ==========================================================================
	   8. Swiper Carousels
	   --------------------------------------------------------------------------
	   Initializes all Swiper instances used across the site. Each carousel type
	   has its own configuration. Helper function wires up prev/next buttons
	   within the parent container.
	   
	   Requires: Swiper.js loaded before this script.
	   ========================================================================== */

	/**
	 * Helper: attach prev/next button click handlers for a Swiper instance
	 * within a parent container.
	 */
	function attachSwiperNav(parentEl, swiperInstance) {
		const prevBtn = parentEl.querySelector('.prev');
		const nextBtn = parentEl.querySelector('.next');

		if (prevBtn) {
			prevBtn.addEventListener('click', function () {
				swiperInstance.slidePrev();
			});
		}
		if (nextBtn) {
			nextBtn.addEventListener('click', function () {
				swiperInstance.slideNext();
			});
		}
	}

	function initSwipers() {
		// Guard: ensure Swiper is available
		if (typeof Swiper === 'undefined') return;

		/* ------------------------------------------------------------------
		   8a. Hero Slider (.slide_hero .swiper)
		   Full-width hero carousel with autoplay and clickable pagination.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.slide_hero .swiper').forEach(function (el) {
			new Swiper(el, {
				loop: true,
				slidesPerView: 1,
				spaceBetween: 0,
				speed: 1000,
				autoplay: {
					delay: 3000
				},
				pagination: {
					el: '.swiper-pagination',
					clickable: true
				}
			});
		});

		/* ------------------------------------------------------------------
		   8b. Range Slider (.range)
		   Horizontal scrolling range/series cards with prev/next navigation.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.range').forEach(function (rangeEl) {
			const swiperEl = rangeEl.querySelector('.swiper');
			if (!swiperEl) return;

			const rangeSwiper = new Swiper(swiperEl, {
				loop: true,
				slidesPerView: 'auto',
				spaceBetween: 0
			});

			attachSwiperNav(rangeEl, rangeSwiper);
		});

		/* ------------------------------------------------------------------
		   8c. Gallery Slider (.gallery .swiper)
		   Image gallery with auto-width slides and pagination dots.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.gallery .swiper').forEach(function (el) {
			new Swiper(el, {
				loop: true,
				slidesPerView: 'auto',
				spaceBetween: 0,
				pagination: {
					el: '.swiper-pagination'
				}
			});
		});

		/* ------------------------------------------------------------------
		   8d. Chosen / Players Slider (.chosen)
		   Artist/player cards with auto-width, small gap, and prev/next nav.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.chosen').forEach(function (chosenEl) {
			const swiperEl = chosenEl.querySelector('.swiper');
			if (!swiperEl) return;

			const chosenSwiper = new Swiper(swiperEl, {
				loop: true,
				slidesPerView: 'auto',
				spaceBetween: 10
			});

			attachSwiperNav(chosenEl, chosenSwiper);
		});

		/* ------------------------------------------------------------------
		   8e. Series Slider (.series_slider)
		   Full-slide series hero carousel with clickable pagination and
		   prev/next navigation arrows.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.series_slider').forEach(function (sliderEl) {
			const swiperEl = sliderEl.querySelector('.swiper');
			if (!swiperEl) return;

			const seriesSwiper = new Swiper(swiperEl, {
				loop: true,
				slidesPerView: 1,
				pagination: {
					el: '.swiper-pagination',
					clickable: true
				}
			});

			attachSwiperNav(sliderEl, seriesSwiper);
		});

		/* ------------------------------------------------------------------
		   8f. Polaroid Slider (.polaroid .swiper)
		   Timeline/polaroid cards with auto-width, spacing, and nav arrows.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.polaroid').forEach(function (polaroidEl) {
			const swiperEl = polaroidEl.querySelector('.swiper');
			if (!swiperEl) return;

			const polaroidSwiper = new Swiper(swiperEl, {
				loop: true,
				slidesPerView: 'auto',
				spaceBetween: 20
			});

			attachSwiperNav(polaroidEl, polaroidSwiper);
		});

		/* ------------------------------------------------------------------
		   8g. Model Gallery (.model_gallery .swiper) — Desktop only
		   Fade-effect gallery for individual model pages. Only initialized
		   on desktop viewports (>= 1024px) where the layout supports it.
		   Includes pagination bullets.
		   ------------------------------------------------------------------ */
		const windowWidth = window.innerWidth;

		document.querySelectorAll('.model_gallery').forEach(function (galleryEl) {
			const swiperEl = galleryEl.querySelector('.swiper');
			if (!swiperEl) return;

				const modelGallerySwiper = new Swiper(swiperEl, {
					loop: true,
					effect: 'fade',
					pagination: {
						type: 'bullets',
						el: '.swiper-pagination'
					}
				});

				attachSwiperNav(galleryEl, modelGallerySwiper);
		});

		/* ------------------------------------------------------------------
		   8h. Model Gallery Full (.model_gallery_full .swiper)
		   Full-width centered gallery with auto-width slides, spacing,
		   pagination bullets, and prev/next navigation.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.model_gallery_full').forEach(function (galleryFullEl) {
			const swiperEl = galleryFullEl.querySelector('.swiper');
			if (!swiperEl) return;

			const fullGallerySwiper = new Swiper(swiperEl, {
				loop: true,
				slidesPerView: 'auto',
				centeredSlides: true,
				spaceBetween: 20,
				pagination: {
					type: 'bullets',
					el: '.swiper-pagination'
				}
			});

			attachSwiperNav(galleryFullEl, fullGallerySwiper);
		});

		/* ------------------------------------------------------------------
		   8i. Series Features (.series_features .swiper)
		   Feature/discography cards. Desktop gets spaceBetween: 30 with
		   prev/next navigation. Mobile gets spaceBetween: 20 without nav.
		   ------------------------------------------------------------------ */
		if (windowWidth >= 1024) {
			// Desktop: wider spacing with navigation arrows
			document.querySelectorAll('.series_features').forEach(function (featureEl) {
				const swiperEl = featureEl.querySelector('.swiper');
				if (!swiperEl) return;

				const featureSwiper = new Swiper(swiperEl, {
					loop: true,
					slidesPerView: 'auto',
					spaceBetween: 30
				});

				attachSwiperNav(featureEl, featureSwiper);
			});
		} else {
			// Mobile: tighter spacing, no navigation arrows
			document.querySelectorAll('.series_features .swiper').forEach(function (swiperEl) {
				new Swiper(swiperEl, {
					loop: true,
					slidesPerView: 'auto',
					spaceBetween: 20
				});
			});
		}

		/* ------------------------------------------------------------------
		   8j. Product Categories (.product_categories .swiper)
		   Horizontal scrolling product category pills/cards.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.product_categories .swiper').forEach(function (el) {
			new Swiper(el, {
				loop: true,
				spaceBetween: 10,
				slidesPerView: 'auto'
			});
		});

		/* ------------------------------------------------------------------
		   8k. Promo Boxes (.promo_boxes .swiper)
		   Promotional content boxes with auto-width slides.
		   ------------------------------------------------------------------ */
		document.querySelectorAll('.promo_boxes .swiper').forEach(function (el) {
			new Swiper(el, {
				loop: true,
				slidesPerView: 'auto',
				spaceBetween: 0
			});
		});
	}


	/* ==========================================================================
	   9. Location Selector (Cookie-based)
	   --------------------------------------------------------------------------
	   On DOMContentLoaded, checks for a 'location' cookie. If not present,
	   shows the .location_selector overlay. The select_country() function is
	   exposed globally on window so it can be called from inline onclick
	   handlers in the template markup.
	   ========================================================================== */

	function getCookie(name) {
		const value = '; ' + document.cookie;
		const parts = value.split('; ' + name + '=');
		if (parts.length === 2) return parts.pop().split(';').shift();
		return null;
	}

	function setCookie(name, value, days) {
		const date = new Date();
		date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
		document.cookie = name + '=' + value + '; path=/; expires=' + date.toUTCString();
	}

	function initLocationSelector() {
		const locationSelector = document.querySelector('.location_selector');

		if (!locationSelector) return;

		// Show the selector if no location cookie is set
		if (!getCookie('location')) {
			locationSelector.classList.add('show');
		}
	}

	// Expose select_country globally so inline onclick handlers can call it
	window.select_country = function (country) {
		if (country) {
			setCookie('location', country, 30);

			// Submit Shopify's localization form to update market/currency
			var input = document.getElementById('localization-country-code');
			if (input) {
				input.value = country;
				input.closest('form').submit();
			}
		}
	};


	/* ==========================================================================
	   10. Product Page Rotate Element
	   --------------------------------------------------------------------------
	   On desktop (>= 1024px), when the #elementToRotate element reaches the
	   vertical center of the viewport, it begins rotating based on continued
	   scroll distance. The rotation is capped at 90 degrees.
	   
	   Simultaneously translates the element on X and Y axes:
	   - positionY caps at 350px, at which point 'show_model_gallery' class
	     is added to body (revealing the model gallery below)
	   - positionX caps at 60px
	   
	   When the element has not yet reached center, its transform resets to
	   the default position.
	   ========================================================================== */

	function initRotateElement() {
		if (window.innerWidth < 1024) return;

		const element = document.getElementById('elementToRotate');
		if (!element) return;

		let beginRotate = false;
		let startScroll = 0;

		function isElementInViewportCenter(el) {
			const rect = el.getBoundingClientRect();
			const elementCenterY = rect.top + rect.height / 2;
			const viewportCenterY = window.innerHeight / 2;
			const threshold = 10;

			return (Math.abs(elementCenterY - viewportCenterY) <= threshold);
		}

		function rotateElementOnScroll() {
			if (isElementInViewportCenter(element) && !beginRotate) {
				beginRotate = true;
				startScroll = window.scrollY;
			} else if (!beginRotate) {
				element.style.transform = 'translateX(0) translateY(0) rotate(0)';
			}

			if (beginRotate) {
				let rotationAngle = (window.scrollY - startScroll) / 2;
				let positionY = rotationAngle * 3.5;
				let positionX = rotationAngle * 3.5;

				// Cap rotation at 90 degrees
				if (rotationAngle >= 90) {
					rotationAngle = 90;
				}

				// Cap Y translation at 350px — triggers gallery reveal
				if (positionY >= 350) {
					positionY = 350;

					if (!document.body.classList.contains('show_model_gallery')) {
						document.body.classList.add('show_model_gallery');
					}
				} else {
					if (document.body.classList.contains('show_model_gallery')) {
						document.body.classList.remove('show_model_gallery');
					}
				}

				// Cap X translation at 60px
				if (positionX >= 60) {
					positionX = 60;
				}

				element.style.transform = 'translateX(-' + positionX + 'px) translateY(' + positionY + 'px) rotate(-' + rotationAngle + 'deg)';
			}
		}

		window.addEventListener('scroll', rotateElementOnScroll);
	}


	/* ==========================================================================
	   11. Dock Model Specs
	   --------------------------------------------------------------------------
	   Handles docking/undocking of spec sections on model product pages.
	   Elements with [data-action="parent"] are monitored on scroll. When their
	   top edge scrolls past the primary header, they receive:
	   
	   - 'fixed' class: the inner header becomes fixed/sticky
	   - 'undocked' class: when the parent's bottom edge passes above the
	     header height, meaning the section has scrolled fully past
	   
	   The 'no_transition' class is temporarily added during class changes to
	   prevent unwanted CSS transition flickers.
	   ========================================================================== */

	function initDockModelSpecs() {
		const parents = document.querySelectorAll('[data-action="parent"]');
		if (parents.length === 0) return;

		function dockModelSpecs() {
			const primaryHeader = document.querySelector('.primary_header');
			let primaryHeaderHeight = 0;

			if (primaryHeader) {
				primaryHeaderHeight = primaryHeader.getBoundingClientRect().height;
				// Override to 0 as per original implementation
				primaryHeaderHeight = 0;
			}

			parents.forEach(function (parent) {
				const parentHeader = parent.querySelector('header');
				if (!parentHeader) return;

				const parentHeaderBottom = parentHeader.getBoundingClientRect().height + primaryHeaderHeight;
				const parentTop = parent.getBoundingClientRect().top;
				const parentBottom = parent.getBoundingClientRect().bottom;

				if (parentBottom <= parentHeaderBottom) {
					// Section has scrolled past — undock
					parent.classList.add('undocked');
					parent.classList.remove('fixed');
				} else if (parentTop <= primaryHeaderHeight) {
					// Section top is at/above header — dock it
					parent.classList.add('no_transition');
					parent.classList.add('fixed');
					parent.classList.remove('undocked');

					setTimeout(function () {
						parent.classList.remove('no_transition');
					}, 10);
				} else {
					// Section is below the header — reset
					parent.classList.add('no_transition');
					parent.classList.remove('fixed');
					parent.classList.remove('undocked');

					setTimeout(function () {
						parent.classList.remove('no_transition');
					}, 10);
				}
			});
		}

		window.addEventListener('scroll', dockModelSpecs);
	}


	/* ==========================================================================
	   12. Collection Filters
	   --------------------------------------------------------------------------
	   Handles the collection filter sidebar interactions:
	   - Mobile: open/close the off-canvas filter drawer
	   - Filter toggle (expand/collapse filter groups)
	   - Checkbox and price range change triggers form submission via URL params
	   - Sort-by dropdown updates the collection sort

	   Uses Shopify's native URL-based filtering with query parameters.
	   ========================================================================== */

	function initCollectionFilters() {
		var filterForm = document.getElementById('collection-filter-form');
		if (!filterForm) return;

		var sidebar = document.getElementById('collection-filters-sidebar');
		var overlay = document.querySelector('.filter_overlay');
		var collectionUrl = filterForm.getAttribute('data-collection-url');

		/* --- Mobile: Open/Close Sidebar --- */

		var openButtons = document.querySelectorAll('[data-action="open_filters"]');
		var closeButtons = document.querySelectorAll('[data-action="close_filters"]');

		function openSidebar() {
			if (sidebar) sidebar.classList.add('open');
			if (overlay) overlay.classList.add('show');
			document.body.style.overflow = 'hidden';
		}

		function closeSidebar() {
			if (sidebar) sidebar.classList.remove('open');
			if (overlay) overlay.classList.remove('show');
			document.body.style.overflow = '';
		}

		openButtons.forEach(function (btn) {
			btn.addEventListener('click', openSidebar);
		});

		closeButtons.forEach(function (btn) {
			btn.addEventListener('click', closeSidebar);
		});

		if (overlay) {
			overlay.addEventListener('click', closeSidebar);
		}

		/* --- Filter Toggle (expand/collapse) --- */

		var filterToggles = filterForm.querySelectorAll('[data-action="toggle_filter"]');

		filterToggles.forEach(function (toggle) {
			toggle.addEventListener('click', function () {
				var isExpanded = toggle.getAttribute('aria-expanded') === 'true';
				var content = toggle.parentElement.querySelector('.filter_content');

				if (isExpanded) {
					toggle.setAttribute('aria-expanded', 'false');
					if (content) content.setAttribute('aria-hidden', 'true');
				} else {
					toggle.setAttribute('aria-expanded', 'true');
					if (content) content.removeAttribute('aria-hidden');
				}
			});
		});

		/* --- Build filter URL from form state --- */

		function buildFilterUrl() {
			var params = [];
			var checkboxes = filterForm.querySelectorAll('input[type="checkbox"]:checked');
			var numberInputs = filterForm.querySelectorAll('input[type="number"]');
			var sortInput = document.getElementById('sort-by-value');

			checkboxes.forEach(function (cb) {
				params.push(encodeURIComponent(cb.name) + '=' + encodeURIComponent(cb.value));
			});

			numberInputs.forEach(function (input) {
				if (input.value && input.value !== '') {
					// Convert to cents for Shopify price filter
					var cents = Math.round(parseFloat(input.value) * 100);
					if (!isNaN(cents) && cents > 0) {
						params.push(encodeURIComponent(input.name) + '=' + cents);
					}
				}
			});

			if (sortInput && sortInput.value) {
				params.push('sort_by=' + encodeURIComponent(sortInput.value));
			}

			var url = collectionUrl;
			if (params.length > 0) {
				url += '?' + params.join('&');
			}

			return url;
		}

		/* --- Apply filters (navigate to URL) --- */

		function applyFilters() {
			window.location.href = buildFilterUrl();
		}

		/* --- Checkbox change handler --- */

		var checkboxes = filterForm.querySelectorAll('input[type="checkbox"]');
		checkboxes.forEach(function (cb) {
			cb.addEventListener('change', applyFilters);
		});

		/* --- Price range debounce handler --- */

		var priceTimer = null;
		var priceInputs = filterForm.querySelectorAll('.price_range input[type="number"]');

		priceInputs.forEach(function (input) {
			input.addEventListener('input', function () {
				if (priceTimer) clearTimeout(priceTimer);
				priceTimer = setTimeout(applyFilters, 800);
			});
		});

		/* --- Sort-by handler --- */

		var sortSelect = document.getElementById('sort-select');
		if (sortSelect) {
			sortSelect.addEventListener('change', function () {
				var sortInput = document.getElementById('sort-by-value');
				if (sortInput) {
					sortInput.value = sortSelect.value;
				}
				applyFilters();
			});
		}

		/* --- Prevent default form submission (use JS-driven navigation) --- */

		filterForm.addEventListener('submit', function (e) {
			e.preventDefault();
			applyFilters();
		});
	}


	/* ==========================================================================
	   12b. Payment Options Modal
	   --------------------------------------------------------------------------
	   Toggles the .payment_options modal when the "Other ways to pay" button
	   is clicked and closes it via the close button.
	   ========================================================================== */

	function initPaymentOptionsModal() {
		var paymentOptions = document.querySelector('.payment_options');
		if (!paymentOptions) return;

		var openButtons = document.querySelectorAll('[data-action="toggle_payment_options"]');
		var closeButtons = document.querySelectorAll('[data-action="close_payment_options"]');

		openButtons.forEach(function (btn) {
			btn.addEventListener('click', function () {
				paymentOptions.classList.add('show');
			});
		});

		closeButtons.forEach(function (btn) {
			btn.addEventListener('click', function () {
				paymentOptions.classList.remove('show');
			});
		});

		// Close when clicking the overlay background
		paymentOptions.addEventListener('click', function (e) {
			if (e.target === paymentOptions) {
				paymentOptions.classList.remove('show');
			}
		});
	}


	/* ==========================================================================
	   12c. Cart Remove Item
	   --------------------------------------------------------------------------
	   Handles the remove button on cart items. Uses Shopify's Cart API to
	   update line item quantity to 0 and reloads the page on success.
	   ========================================================================== */

	function initCartRemoveButtons() {
		var removeButtons = document.querySelectorAll('.basket .remove_item');
		if (removeButtons.length === 0) return;

		removeButtons.forEach(function (btn) {
			btn.addEventListener('click', function (e) {
				e.preventDefault();
				e.stopPropagation();

				var line = btn.getAttribute('data-line');

				fetch('/cart/change.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ line: parseInt(line, 10), quantity: 0 })
				}).then(function () {
					window.location.reload();
				});
			});
		});
	}


	/* ==========================================================================
	   Initialization
	   --------------------------------------------------------------------------
	   Run all initializers. Scroll-dependent animations (fade-in, parallax,
	   gallery expand) are deferred slightly via window.load + setTimeout to
	   ensure accurate layout measurements after all assets have loaded.
	   ========================================================================== */

	// Initialize immediately on DOMContentLoaded
	initSwipers();
	initLocationSelector();
	initRotateElement();
	initDockModelSpecs();
	initCollectionFilters();
	initPaymentOptionsModal();
	initCartRemoveButtons();

	// Defer scroll-driven animations until after full page load
	// to ensure accurate element position measurements
	window.addEventListener('load', function () {
		setTimeout(function () {
			initFadeInElements();
			initParallaxElements();
			initGalleryExpand();
		}, 100);
	});

});
